import { useTradingSettings } from '@src/providers/AppConfigProvider';
import {
  Box,
  Dialog,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  IndicatorBadge,
  LoaderSizes,
  LoaderTalos,
  NotificationVariants,
  OrdStatusEnum,
  request,
  Stack,
  Text,
  Toggle,
  Tooltip,
  useCallbackRef,
  useEndpointsContext,
  useGlobalToasts,
  usePortal,
  VStack,
  type CareOrder,
  type UseDisclosureWithContextReturn,
} from '@talos/kyoko';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { CareOrderNettingHeader } from './CareOrderNettingHeader';
import { CompatibleCareOrdersBlotter } from './CompatibleCareOrdersBlotter';
import { getCompatibleCareOrders } from './getCompatibleCareOrders';
import { MemberCareOrdersBlotter } from './MemberCareOrdersBlotter';
import { PreviewBlotter } from './PreviewBlotter';
import { useSelectableCareOrders } from './useSelectableCareOrders';
import {
  CareOrderNettingPreviewMode,
  useWorkingOrderPreview,
  type CareOrderNettingPreviewMode as CareOrderNettingPreviewModeType,
} from './useWorkingOrderPreview';

export { CareOrderNettingPreviewMode };

export interface CareOrderNettingPreviewDialogContext {
  /** We have two modes - creating a new netted care order, or modifying an existing netted care order */
  mode: CareOrderNettingPreviewModeType;
  /** The starting selected care orders to prime the dialog with */
  selectedCareOrders: CareOrder[];
  /** Pass if modifying */
  nettedCareOrder?: CareOrder;
}

interface CareOrderNettingPreviewDialogProps {
  disclosure: UseDisclosureWithContextReturn<CareOrderNettingPreviewDialogContext>;
}

export function CareOrderNettingPreviewDialog({ disclosure }: CareOrderNettingPreviewDialogProps) {
  const context = disclosure.context;
  if (disclosure.openId == null || context == null) {
    return null;
  }

  return <CareOrderNettingPreviewDialogSession key={disclosure.openId} disclosure={disclosure} context={context} />;
}

interface CareOrderNettingPreviewDialogSessionProps {
  disclosure: UseDisclosureWithContextReturn<CareOrderNettingPreviewDialogContext>;
  context: CareOrderNettingPreviewDialogContext;
}

const getPreviewSectionTitle = (mode: CareOrderNettingPreviewModeType) =>
  mode === CareOrderNettingPreviewMode.Modify ? 'Netting Modification Preview' : 'Preview Netted Order';

function CareOrderNettingPreviewDialogSession({ disclosure, context }: CareOrderNettingPreviewDialogSessionProps) {
  const { mode } = context;
  const { orgApiEndpoint } = useEndpointsContext();
  const { careOrderNettingPreviewShowAllDecimals, setCareOrderNettingPreviewShowAllDecimals } = useTradingSettings();
  const { add: addToast } = useGlobalToasts();
  const memberCareOrdersFiltersPortalId = 'care-order-netting-member-care-orders-filters';
  const compatibleCareOrdersFiltersPortalId = 'care-order-netting-compatible-care-orders-filters';
  const { setPortalRef: setMemberCareOrdersFiltersPortalRef } = usePortal(memberCareOrdersFiltersPortalId);
  const { setPortalRef: setCompatibleCareOrdersFiltersPortalRef } = usePortal(compatibleCareOrdersFiltersPortalId);
  const isModifyMode = mode === CareOrderNettingPreviewMode.Modify;

  const nettedCareOrder = isModifyMode ? context.nettedCareOrder : undefined;
  const minimumSelectedCareOrders = isModifyMode ? 1 : 2;

  const [isLoading, setIsLoading] = useState(false);
  const [showCompatibleCareOrders, setShowCompatibleCareOrders] = useState(false);
  const { allCareOrders, careOrdersByID, selectedCareOrderIDs, onSelectionChanged } = useSelectableCareOrders({
    mode,
    selectedCareOrders: context.selectedCareOrders,
    nettedCareOrder,
  });

  const {
    workingOrder: previewWorkingOrder,
    loading: previewLoading,
    previewError,
  } = useWorkingOrderPreview({
    mode,
    careOrderIDs: selectedCareOrderIDs,
    orderID: nettedCareOrder?.OrderID,
    addToast,
  });

  const isPreviewMissing = previewWorkingOrder == null;
  const isPreviewStatusRejected =
    previewWorkingOrder != null && previewWorkingOrder.OrdStatus === OrdStatusEnum.Rejected;
  const previewActionLabel = isModifyMode ? 'modify netted order' : 'net care orders';
  const previewInvalidTooltip =
    isPreviewMissing || isPreviewStatusRejected
      ? `Unable to ${previewActionLabel} because preview order ${
          isPreviewMissing ? 'is unavailable' : `status is ${previewWorkingOrder.OrdStatus}`
        }.`
      : undefined;

  const confirmDisabled =
    selectedCareOrderIDs.length < minimumSelectedCareOrders || isLoading || isPreviewMissing || isPreviewStatusRejected;

  const handleConfirm = useCallbackRef(async () => {
    setIsLoading(true);
    try {
      if (isModifyMode) {
        if (nettedCareOrder == null) {
          return;
        }

        await request('PATCH', `${orgApiEndpoint}/care/working/orders`, {
          ClOrdId: uuid(),
          OrderID: nettedCareOrder.OrderID,
          MemberCareOrders: selectedCareOrderIDs,
        });
      } else {
        await request('POST', `${orgApiEndpoint}/care/working/orders`, {
          ClOrdId: uuid(),
          MemberCareOrders: selectedCareOrderIDs,
        });
      }
      addToast({
        text: isModifyMode
          ? `Successfully modified netted order with ${selectedCareOrderIDs.length} care order(s).`
          : `Successfully netted ${selectedCareOrderIDs.length} care orders.`,
        variant: NotificationVariants.Positive,
      });
      disclosure.close();
    } catch (error) {
      addToast({
        text: `Could not ${previewActionLabel}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: NotificationVariants.Negative,
      });
    } finally {
      setIsLoading(false);
    }
  });

  const memberCareOrdersForHeader = useMemo(() => {
    if (careOrdersByID == null) {
      return context.selectedCareOrders;
    }

    return selectedCareOrderIDs
      .map(orderID => careOrdersByID.get(orderID))
      .filter((careOrder): careOrder is CareOrder => careOrder != null);
  }, [careOrdersByID, context.selectedCareOrders, selectedCareOrderIDs]);
  const memberCareOrdersCount = selectedCareOrderIDs.length;
  const compatibleCareOrdersCount = useMemo(
    () => getCompatibleCareOrders({ allCareOrders, isModificationMode: isModifyMode }).length,
    [allCareOrders, isModifyMode]
  );
  const showAllDecimals = careOrderNettingPreviewShowAllDecimals;

  return (
    <Dialog
      isOpen={disclosure.isOpen}
      close={disclosure.close}
      onCloseComplete={() => {
        disclosure.onCloseComplete();
      }}
      onConfirm={handleConfirm}
      confirmLoading={isLoading}
      closeOnConfirm={false}
      title="Care Order Netting Preview"
      headerSuffix={
        <Toggle
          label="Show All Decimals"
          size={FormControlSizes.Small}
          checked={showAllDecimals}
          onChange={setCareOrderNettingPreviewShowAllDecimals}
          data-testid="care-order-netting-preview-show-decimals-toggle"
        />
      }
      confirmLabel={isModifyMode ? 'Modify Netted Order' : `Net ${selectedCareOrderIDs.length} Care Orders`}
      confirmDisabled={confirmDisabled}
      confirmButtonTooltip={previewInvalidTooltip}
      minWidth="960px"
      width="90vw"
      maxWidth="1600px"
      minHeight="750px"
      height="90vh"
      style={{ maxHeight: '900px' }}
      contentPadding="0px"
      dataTestId="care-order-netting-preview-dialog"
      showClose
    >
      <VStack
        w="100%"
        flex={1}
        alignItems="stretch"
        data-testid="care-order-netting-preview"
        gap="spacing1"
        background="backgroundModal"
      >
        <CareOrderNettingHeader
          workingOrder={previewWorkingOrder}
          memberCareOrders={memberCareOrdersForHeader}
          showAllDecimals={showAllDecimals}
        />
        <Box h="100%">
          <Stack h="100%">
            <BlotterHeader>
              <HStack gap="spacing6">
                <Text color="colorTextImportant" fontSize="fontSizeSm">
                  {getPreviewSectionTitle(mode)}
                </Text>
                {previewError != null ? (
                  <Tooltip tooltip={previewError}>
                    <Box data-testid="care-order-netting-preview-error-icon">
                      <Icon icon={IconName.ExclamationCircle} color="colors.red.lighten" />
                    </Box>
                  </Tooltip>
                ) : (
                  previewLoading && (
                    <Box>
                      <LoaderTalos size={LoaderSizes.XXS} />
                    </Box>
                  )
                )}
              </HStack>
            </BlotterHeader>
            <Box h="80px" data-testid="care-order-netting-preview-working-order">
              <PreviewBlotter previewWorkingOrder={previewWorkingOrder} showAllDecimals={showAllDecimals} />
            </Box>

            <Stack flex={1}>
              <BlotterHeader>
                <HStack gap="spacing6">
                  <Text fontSize="fontSizeSm" color="colorTextImportant">
                    Member Care Orders
                  </Text>
                  <IndicatorBadge>{memberCareOrdersCount}</IndicatorBadge>
                </HStack>
                <Box ref={setMemberCareOrdersFiltersPortalRef} />
              </BlotterHeader>
              <MemberCareOrdersBlotter
                portalId={memberCareOrdersFiltersPortalId}
                isModificationMode={isModifyMode}
                showAllDecimals={showAllDecimals}
                allCareOrders={allCareOrders}
                onSelectionChanged={onSelectionChanged}
                onShowCompatibleCareOrders={() => setShowCompatibleCareOrders(true)}
                showCompatibleCareOrders={showCompatibleCareOrders}
              />
            </Stack>
            {showCompatibleCareOrders && (
              <Stack flex={1}>
                <BlotterHeader>
                  <HStack gap="spacing6">
                    <Text fontSize="fontSizeSm" color="colorTextImportant">
                      Compatible Care Orders
                    </Text>
                    <IndicatorBadge>{compatibleCareOrdersCount}</IndicatorBadge>
                  </HStack>
                  <Box ref={setCompatibleCareOrdersFiltersPortalRef} />
                </BlotterHeader>
                <CompatibleCareOrdersBlotter
                  portalId={compatibleCareOrdersFiltersPortalId}
                  isModificationMode={isModifyMode}
                  showAllDecimals={showAllDecimals}
                  allCareOrders={allCareOrders}
                  onSelectionChanged={onSelectionChanged}
                  onClose={() => setShowCompatibleCareOrders(false)}
                />
              </Stack>
            )}
          </Stack>
        </Box>
      </VStack>
    </Dialog>
  );
}

const BlotterHeader = styled(HStack)`
  height: 40px;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing6}px;
  padding: 0 ${({ theme }) => theme.spacing16}px;
  border-top: 1px solid ${({ theme }) => theme.backgroundBody};
  border-bottom: 1px solid ${({ theme }) => theme.backgroundBody};
`;
